import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { helpGroups, HELP_ARTICLES } from "./corpus";

/**
 * Каркас раздела «Справка» (доработка по приёмке 22.08.2026): слева ПОСТОЯННО видимое
 * оглавление-дерево, справа — содержимое (статья либо поиск по корпусу).
 *
 * ПОЧЕМУ ДЕРЕВО ПОСТОЯННОЕ, А НЕ «СПИСОК → СТАТЬЯ → НАЗАД». В первой редакции оглавление было
 * ОТДЕЛЬНЫМ экраном: открыв статью, человек терял из виду и остальные, и тему, в которой
 * находится. Справку читают не подряд — в неё заходят с вопросом и перебирают соседние статьи,
 * и каждый такой перебор стоил двух переходов. Дерево слева убирает переходы совсем: соседняя
 * статья — одно нажатие, и всё время видно, где ты.
 *
 * Прокрутка у дерева СВОЯ (`sticky` + собственный `overflow-y`): длинная статья не должна
 * утаскивать оглавление за верхний край, иначе постоянное дерево постоянным быть перестаёт.
 */

/**
 * Ширина колонки оглавления. Уже — заголовки статей начнут переноситься по три строки.
 *
 * С CR-5 это тот же токен, что у боковой панели разделов (240 вместо прежних 252): в разделе
 * «Справка» две навигационные колонки стоят рядом, и разная ширина читалась бы как небрежность.
 * Самый длинный заголовок («Администрирование: пользователи и справочники») занимал две строки
 * и раньше — проверка на площадке в чек-листе docs/17 §Б9.
 */
const TREE_WIDTH = "lg:w-[var(--w-nav)]";

export function HelpLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <nav aria-label="Оглавление справки" className={`no-print shrink-0 ${TREE_WIDTH}`}>
        <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-2">
          {helpGroups().map((group) => (
            <div key={group} className="mb-4">
              <p className="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-muted">
                {group}
              </p>
              <ul className="space-y-0.5">
                {HELP_ARTICLES.filter((a) => a.group === group).map((article) => {
                  const to = `/help/${article.id}`;
                  const current = pathname === to;
                  return (
                    <li key={article.id}>
                      <Link
                        to={to}
                        aria-current={current ? "page" : undefined}
                        className={
                          current
                            ? "block rounded-[var(--radius-m)] bg-control-bg px-2 py-1 text-[13px] font-bold text-accent"
                            : "block rounded-[var(--radius-m)] px-2 py-1 text-[13px] text-ink hover:bg-bg"
                        }
                      >
                        {article.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {/* `min-w-0` обязателен: без него длинная строка внутри статьи распирает колонку и ломает
          раскладку — flex-элемент по умолчанию не даёт содержимому сжиматься. */}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
